import { useState, useMemo } from 'react';
import {
  Plus,
  Search,
  ListFilter,
  ClipboardList,
  BookTemplate,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CriteriaGridSkeleton } from '@/components/ui/page-skeletons';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CriteriaBuilder } from '@/features/criteria/components/CriteriaBuilder';
import { CriterionCard } from '@/features/criteria/components/CriterionCard';
import { TemplateList } from '@/features/criteria/components/TemplateList';
import { useCriteria, useDeleteCriterion } from '@/features/criteria/hooks/useCriteria';
import type { Criterion, CriterionType } from '@/features/criteria/types';
import { CRITERION_TYPE_LABELS } from '@/features/criteria/types';

const ALL_TYPES: CriterionType[] = ['boolean', 'scale', 'text_match', 'pico', 'bias_risk'];

export function Criteria() {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<Criterion | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const { data: criteria, isLoading } = useCriteria();
  const deleteCriterion = useDeleteCriterion();

  // Derive available categories from user criteria
  const categories = useMemo(() => {
    if (!criteria) return [];
    const cats = new Set<string>();
    for (const c of criteria) {
      if (c.category) cats.add(c.category);
    }
    return Array.from(cats).sort();
  }, [criteria]);

  // Filter and search criteria
  const filteredCriteria = useMemo(() => {
    if (!criteria) return [];
    return criteria.filter((c) => {
      if (filterType !== 'all' && c.type !== filterType) return false;
      if (filterCategory !== 'all' && c.category !== filterCategory) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesName = c.name.toLowerCase().includes(q);
        const matchesDesc = c.description?.toLowerCase().includes(q);
        if (!matchesName && !matchesDesc) return false;
      }
      return true;
    });
  }, [criteria, filterType, filterCategory, searchQuery]);

  const handleEdit = (criterion: Criterion) => {
    setEditingCriterion(criterion);
    setBuilderOpen(true);
  };

  const handleDelete = async (criterionId: string) => {
    try {
      await deleteCriterion.mutateAsync(criterionId);
    } catch {
      // toast handled in useDeleteCriterion onError
    }
  };

  const handleBuilderClose = (open: boolean) => {
    setBuilderOpen(open);
    if (!open) setEditingCriterion(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Criteria</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Define how sources should be evaluated. Use templates or create your own.
          </p>
        </div>
        <Button onClick={() => setBuilderOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Criterion
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="my-criteria" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-criteria" className="gap-2">
            <ClipboardList className="h-4 w-4" />
            My Criteria
            {criteria && criteria.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">
                {criteria.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <BookTemplate className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* My Criteria tab */}
        <TabsContent value="my-criteria" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search criteria..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <ListFilter className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {ALL_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {CRITERION_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categories.length > 0 && (
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Loading state */}
          {isLoading && <CriteriaGridSkeleton count={6} />}

          {/* Empty state */}
          {!isLoading && criteria && criteria.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border-2 border-dashed rounded-xl">
              <ClipboardList className="h-12 w-12 mb-4 opacity-40" />
              <h3 className="font-medium text-foreground">No criteria yet</h3>
              <p className="text-sm mt-1 max-w-md text-center">
                Create your first criterion or browse templates to get started with evaluating sources.
              </p>
              <div className="flex gap-3 mt-4">
                <Button onClick={() => setBuilderOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Criterion
                </Button>
              </div>
            </div>
          )}

          {/* No results for current filter */}
          {!isLoading && criteria && criteria.length > 0 && filteredCriteria.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No matching criteria</p>
              <p className="text-xs mt-1">Try adjusting your search or filters.</p>
            </div>
          )}

          {/* Criteria grid */}
          {filteredCriteria.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCriteria.map((c) => (
                <CriterionCard
                  key={c.id}
                  criterion={c}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={deleteCriterion.isPending}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Templates tab */}
        <TabsContent value="templates">
          <TemplateList />
        </TabsContent>
      </Tabs>

      {/* Builder dialog */}
      <CriteriaBuilder
        open={builderOpen}
        onOpenChange={handleBuilderClose}
        criterion={editingCriterion}
      />
    </div>
  );
}
